import React, { useEffect, useState, Component } from 'react';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, ComposedChart } from 'recharts';
import { HomeIcon, DollarSignIcon, TrendingUpIcon, CreditCardIcon, LandmarkIcon, TargetIcon, CloudIcon, RefreshCwIcon, ArrowUpRightIcon, ArrowDownRightIcon, AlertTriangleIcon, LightbulbIcon, CheckCircle2Icon, BuildingIcon, CircleDotIcon, SunIcon, MoonIcon, MegaphoneIcon, UsersIcon, FilterIcon, ClockIcon } from 'lucide-react';
// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pipelineData = months.map((m, i) => ({
  month: m,
  leads: Math.round(420 + Math.sin(i * 0.6) * 80 + i * 15),
  converted: Math.round(110 + Math.sin(i * 0.5) * 25 + i * 4),
  registered: Math.round(85 + Math.sin(i * 0.4) * 18 + i * 3),
  closed: Math.round(62 + Math.sin(i * 0.3) * 12 + i * 2)
}));
const marginData = months.map((m, i) => ({
  month: m,
  grossMargin: +(2.1 + Math.sin(i * 0.7) * 0.4 + i * 0.04).toFixed(2),
  netMargin: +(1.3 + Math.sin(i * 0.6) * 0.3 + i * 0.03).toFixed(2),
  salesPremium: +(0.8 + Math.sin(i * 0.5) * 0.2 + i * 0.01).toFixed(2)
}));
const warehouseData = months.map((m, i) => ({
  month: m,
  lineUsed: Math.round(18 + Math.sin(i * 0.8) * 4 + i * 0.5),
  lineLimit: 25,
  interestCost: +(0.42 + Math.sin(i * 0.4) * 0.08 + i * 0.005).toFixed(3),
  interestGain: +(0.58 + Math.sin(i * 0.3) * 0.06 + i * 0.006).toFixed(3)
}));
const creditPullData = months.map((m, i) => ({
  month: m,
  pulls: Math.round(390 + Math.sin(i * 0.6) * 70 + i * 14),
  cost: Math.round((390 + Math.sin(i * 0.6) * 70 + i * 14) * 38.5),
  wasted: Math.round((390 + Math.sin(i * 0.6) * 70 + i * 14) * 38.5 * (0.28 - i * 0.01))
}));
const loanTypeBreakdown = [{
  name: 'Conventional',
  value: 44,
  color: '#4F46E5'
}, {
  name: 'FHA',
  value: 22,
  color: '#0EA5E9'
}, {
  name: 'VA',
  value: 18,
  color: '#10B981'
}, {
  name: 'Jumbo',
  value: 10,
  color: '#8B5CF6'
}, {
  name: 'USDA',
  value: 6,
  color: '#F59E0B'
}];
// Lead Intelligence data
const leadSources = [{
  source: 'Google Ads',
  leads: 1840,
  cpl: 62,
  qualified: 612,
  closed: 168,
  color: '#4F46E5'
}, {
  source: 'Facebook',
  leads: 1520,
  cpl: 48,
  qualified: 412,
  closed: 98,
  color: '#0EA5E9'
}, {
  source: 'Referral',
  leads: 980,
  cpl: 18,
  qualified: 624,
  closed: 312,
  color: '#10B981'
}, {
  source: 'Organic',
  leads: 1240,
  cpl: 9,
  qualified: 388,
  closed: 142,
  color: '#8B5CF6'
}, {
  source: 'Direct Mail',
  leads: 720,
  cpl: 84,
  qualified: 174,
  closed: 51,
  color: '#F59E0B'
}, {
  source: 'Aggregators',
  leads: 543,
  cpl: 112,
  qualified: 89,
  closed: 22,
  color: '#F43F5E'
}];
const monthlyLeadEconomics = months.map((m, i) => ({
  month: m,
  spend: Math.round(180000 + Math.sin(i * 0.5) * 30000 + i * 4500),
  leads: Math.round(520 + Math.sin(i * 0.6) * 70 + i * 12),
  closed: Math.round(195 + Math.sin(i * 0.4) * 22 + i * 4)
}));
const leadStageFunnel = [{
  stage: 'Leads Captured',
  count: 6843,
  pct: 100,
  color: '#4F46E5'
}, {
  stage: 'Contacted (24h)',
  count: 5482,
  pct: 80.1,
  color: '#0EA5E9'
}, {
  stage: 'Qualified',
  count: 2299,
  pct: 33.6,
  color: '#10B981'
}, {
  stage: 'Application Filed',
  count: 1834,
  pct: 26.8,
  color: '#8B5CF6'
}, {
  stage: 'Closed Loan',
  count: 793,
  pct: 11.6,
  color: '#F59E0B'
}];
const leadAging = [{
  bucket: '<1 hr',
  count: 2410,
  color: '#10B981'
}, {
  bucket: '1–4 hr',
  count: 1840,
  color: '#4F46E5'
}, {
  bucket: '4–24 hr',
  count: 1230,
  color: '#0EA5E9'
}, {
  bucket: '1–3 d',
  count: 812,
  color: '#F59E0B'
}, {
  bucket: '>3 d',
  count: 551,
  color: '#F43F5E'
}];
type KpiItem = {
  label: string;
  value: string;
  delta: string;
  up: boolean;
  Icon: ComponentType<{
    className?: string;
  }>;
};
const kpis: KpiItem[] = [{
  label: 'YTD Loans Closed',
  value: '2,847',
  delta: '+18.4%',
  up: true,
  Icon: HomeIcon
}, {
  label: 'Total Volume',
  value: '$1.24B',
  delta: '+22.1%',
  up: true,
  Icon: DollarSignIcon
}, {
  label: 'Avg Net Margin',
  value: '1.61%',
  delta: '+0.12%',
  up: true,
  Icon: TrendingUpIcon
}, {
  label: 'Credit Pull Cost YTD',
  value: '$4.2M',
  delta: '-6.3%',
  up: false,
  Icon: CreditCardIcon
}, {
  label: 'Warehouse Line Used',
  value: '79.2%',
  delta: '+4.1%',
  up: false,
  Icon: LandmarkIcon
}, {
  label: 'Lead Conversion',
  value: '26.8%',
  delta: '+2.3%',
  up: true,
  Icon: TargetIcon
}];
const sfSyncTime = 'Today, 9:47 AM';
// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt$ = (n: number) => n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : `$${(n / 1e3).toFixed(0)}K`;
const stageColor: Record<string, string> = {
  'Closed Won': '#10B981',
  Negotiation: '#4F46E5',
  Proposal: '#0EA5E9',
  Qualification: '#F59E0B',
  Discovery: '#8B5CF6'
};
// ─── THEME HOOK ───────────────────────────────────────────────────────────────
function useDarkMode() {
  const [dark, setDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('dashboard-theme');
    if (stored) return stored === 'dark';
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });
  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add('dark');else root.classList.remove('dark');
    localStorage.setItem('dashboard-theme', dark ? 'dark' : 'light');
  }, [dark]);
  return {
    dark,
    toggle: () => setDark(d => !d)
  };
}
// ─── TOOLTIPS ─────────────────────────────────────────────────────────────────
const CustomTooltip = ({
  active,
  payload,
  label,
  suffix = '',
  prefix = '',
  formatter
}: any) => {
  if (!active || !payload?.length) return null;
  const formatValue = (p: any) => {
    if (typeof formatter === 'function') {
      const out = formatter(p.value, p.name, p);
      if (Array.isArray(out)) return out[0];
      return out;
    }
    const v = typeof p.value === 'number' && p.value > 1000 ? p.value.toLocaleString() : p.value;
    return `${prefix}${v}${suffix}`;
  };
  return <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:shadow-black/40" data-id="element-1">
      <div className="mb-1.5 text-xs font-semibold text-slate-900 dark:text-slate-100" data-id="element-2">
        {label}
      </div>
      {payload.map((p: any, i: number) => <div key={i} className="flex items-center justify-between gap-4 text-xs" data-id="element-3">
          <div className="flex items-center gap-1.5" data-id="element-4">
            <span className="inline-block h-2 w-2 rounded-sm" style={{
          background: p.color
        }} data-id="element-5" />
            <span className="text-slate-500 dark:text-slate-400" data-id="element-6">{p.name}</span>
          </div>
          <span className="font-mono-num font-semibold text-slate-900 dark:text-slate-100" data-id="element-7">
            {formatValue(p)}
          </span>
        </div>)}
    </div>;
};
// ─── ATOMS ────────────────────────────────────────────────────────────────────
function KPICard({
  label,
  value,
  delta,
  up,
  Icon,
  idx
}: KpiItem & {
  idx: number;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), idx * 60);
    return () => clearTimeout(t);
  }, [idx]);
  return <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-indigo-500/40 dark:hover:shadow-black/40" style={{
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(12px)'
  }} data-id="element-8">
      <div className="flex items-start justify-between" data-id="element-9">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400" data-id="element-10">
          <Icon className="h-4 w-4" data-id="element-11" />
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold font-mono-num ${up ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' : 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400'}`} data-id="element-12">
          {up ? <ArrowUpRightIcon className="h-3 w-3" data-id="element-13" /> : <ArrowDownRightIcon className="h-3 w-3" data-id="element-14" />}
          {delta}
        </span>
      </div>
      <div className="mt-4 text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400" data-id="element-15">
        {label}
      </div>
      <div className="mt-1 font-mono-num text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100" data-id="element-16">
        {value}
      </div>
      <div className="mt-1 text-[11px] text-slate-400 dark:text-slate-500" data-id="element-17">
        vs prior year
      </div>
    </div>;
}
function SectionHeader({
  title,
  subtitle,
  action
}: {
  title: string;
  subtitle?: string;
  action?: string;
}) {
  return <div className="mb-4 flex items-end justify-between" data-id="element-18">
      <div data-id="element-19">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" data-id="element-20">
          {title}
        </div>
        {subtitle && <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400" data-id="element-21">
            {subtitle}
          </div>}
      </div>
      {action && <button className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300" data-id="element-22">
          {action} <ArrowUpRightIcon className="h-3 w-3" data-id="element-23" />
        </button>}
    </div>;
}
function ChartCard({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 ${className}`} data-id="element-24">
      {children}
    </div>;
}
function StatCard({
  label,
  value,
  sub,
  color
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return <ChartCard data-id="element-25">
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400" data-id="element-26">
        {label}
      </div>
      <div className={`mt-2 font-mono-num text-2xl font-bold ${color}`} data-id="element-27">
        {value}
      </div>
      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-id="element-28">
        {sub}
      </div>
    </ChartCard>;
}
// ─── MAIN ─────────────────────────────────────────────────────────────────────
export function MortgageDashboard() {
  const {
    dark,
    toggle
  } = useDarkMode();
  const [activeTab, setActiveTab] = useState('overview');
  const [sfConnected] = useState(true);
  const [sfSyncing, setSfSyncing] = useState(false);
  const handleSfSync = () => {
    setSfSyncing(true);
    setTimeout(() => setSfSyncing(false), 2200);
  };
  // theme-aware chart tokens
  const gridStroke = dark ? '#1E293B' : '#E2E8F0';
  const tickFill = dark ? '#64748B' : '#94A3B8';
  const tooltipBg = dark ? '#0F172A' : '#FFFFFF';
  const tooltipBd = dark ? '#334155' : '#E2E8F0';
  const pieStroke = dark ? '#0F172A' : '#FFFFFF';
  const axisProps = {
    tick: {
      fill: tickFill,
      fontSize: 11,
      fontFamily: 'JetBrains Mono'
    },
    axisLine: false as const,
    tickLine: false as const
  };
  const inlineTooltipStyle = {
    background: tooltipBg,
    border: `1px solid ${tooltipBd}`,
    borderRadius: 8,
    fontSize: 11,
    color: dark ? '#E2E8F0' : '#0F172A'
  };
  const legendStyle = {
    fontSize: 11,
    color: dark ? '#94A3B8' : '#64748B'
  };
  const tabs = [{
    id: 'overview',
    label: 'Executive Overview'
  }, {
    id: 'pipeline',
    label: 'Loan Pipeline'
  }, {
    id: 'margins',
    label: 'Margins & Profitability'
  }, {
    id: 'warehouse',
    label: 'Warehouse Line'
  }, {
    id: 'leads',
    label: 'Lead Intelligence'
  }];
  return <div className="min-h-screen w-full bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100" data-id="element-29">
      {/* HEADER */}
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-950/80" data-id="element-30">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-8" data-id="element-31">
          <div className="flex items-center gap-3" data-id="element-32">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-600 to-indigo-500 text-white shadow-sm" data-id="element-33">
              <BuildingIcon className="h-4 w-4" data-id="element-34" />
            </div>
            <div data-id="element-35">
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" data-id="element-36">
                Mortgage Intelligence Suite
              </div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400" data-id="element-37">
                COO / CFO Executive Dashboard
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-38">
            <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${sfConnected ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' : 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400'}`} data-id="element-39">
              <span className={`h-1.5 w-1.5 rounded-full ${sfConnected ? 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)]' : 'bg-rose-500'}`} data-id="element-40" />
              {sfConnected ? 'Salesforce live' : 'SF disconnected'}
            </div>
            <button onClick={handleSfSync} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800" data-id="element-41">
              <RefreshCwIcon className={`h-3.5 w-3.5 ${sfSyncing ? 'animate-spin' : ''}`} data-id="element-42" />
              {sfSyncing ? 'Syncing…' : 'Sync SF'}
            </button>
            <div className="font-mono-num text-[11px] text-slate-400 dark:text-slate-500" data-id="element-43">
              Last sync: {sfSyncTime}
            </div>
            <button onClick={toggle} aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'} title={dark ? 'Switch to light mode' : 'Switch to dark mode'} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 hover:text-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-indigo-400" data-id="element-44">
              {dark ? <SunIcon className="h-4 w-4" data-id="element-45" /> : <MoonIcon className="h-4 w-4" data-id="element-46" />}
            </button>
          </div>
        </div>

        <nav className="mx-auto max-w-[1600px] px-8" data-id="element-47">
          <div className="flex gap-1" data-id="element-48">
            {tabs.map(t => <button key={t.id} onClick={() => setActiveTab(t.id)} className={`relative px-3 py-3 text-sm font-medium transition-colors ${activeTab === t.id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'}`} data-id="element-49">
                {t.label}
                {activeTab === t.id && <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-indigo-600 dark:bg-indigo-400" data-id="element-50" />}
              </button>)}
          </div>
        </nav>
      </header>

      {/* CONTENT */}
      <main className="mx-auto max-w-[1600px] px-8 py-7" data-id="element-51">
        {/* ═══ OVERVIEW ═══ */}
        {activeTab === 'overview' && <div className="space-y-5" data-id="element-52">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6" data-id="element-53">
              {kpis.map((k, i) => <KPICard key={i} {...k} idx={i} data-id="element-54" />)}
            </div>

            <div className="grid gap-5 lg:grid-cols-3" data-id="element-55">
              <ChartCard className="lg:col-span-2" data-id="element-56">
                <SectionHeader title="Full Loan Pipeline — YTD" subtitle="Leads → Converted → Registered → Closed" action="Export CSV" data-id="element-57" />
                <ResponsiveContainer width="100%" height={260} data-id="element-58">
                  <ComposedChart data={pipelineData} data-id="element-59">
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-60" />
                    <XAxis dataKey="month" {...axisProps} data-id="element-61" />
                    <YAxis {...axisProps} data-id="element-62" />
                    <Tooltip content={<CustomTooltip data-id="element-64" />} data-id="element-63" />
                    <Legend wrapperStyle={legendStyle} iconType="circle" data-id="element-65" />
                    <Area type="monotone" dataKey="leads" fill={dark ? '#3730A3' : '#E0E7FF'} stroke="#818CF8" strokeWidth={1.5} name="Leads" fillOpacity={dark ? 0.35 : 1} data-id="element-66" />
                    <Bar dataKey="converted" fill="#4F46E5" name="Converted" radius={[4, 4, 0, 0]} barSize={14} data-id="element-67" />
                    <Line type="monotone" dataKey="registered" stroke="#10B981" strokeWidth={2} dot={false} name="Registered" data-id="element-68" />
                    <Line type="monotone" dataKey="closed" stroke={dark ? '#F8FAFC' : '#0F172A'} strokeWidth={2.5} dot={{
                  fill: dark ? '#F8FAFC' : '#0F172A',
                  r: 3
                }} name="Closed" data-id="element-69" />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard data-id="element-70">
                <SectionHeader title="Loan Type Mix" subtitle="By closed volume" data-id="element-71" />
                <ResponsiveContainer width="100%" height={200} data-id="element-72">
                  <PieChart data-id="element-73">
                    <Pie data={loanTypeBreakdown} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value" data-id="element-74">
                      {loanTypeBreakdown.map((e, i) => <Cell key={i} fill={e.color} stroke={pieStroke} strokeWidth={2} data-id="element-75" />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip formatter={(v: any) => `${v}%`} data-id="element-77" />} data-id="element-76" />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5" data-id="element-78">
                  {loanTypeBreakdown.map((e, i) => <div key={i} className="flex items-center gap-1.5" data-id="element-79">
                      <span className="h-2 w-2 rounded-sm" style={{
                  background: e.color
                }} data-id="element-80" />
                      <span className="text-[11px] text-slate-600 dark:text-slate-400" data-id="element-81">
                        {e.name}{' '}
                        <span className="font-mono-num font-semibold text-slate-900 dark:text-slate-100" data-id="element-82">
                          {e.value}%
                        </span>
                      </span>
                    </div>)}
                </div>
              </ChartCard>
            </div>

            <div className="grid gap-5 lg:grid-cols-2" data-id="element-83">
              <ChartCard data-id="element-84">
                <SectionHeader title="Net Margin Trend" subtitle="Gross / Net / Sales Premium (%)" data-id="element-85" />
                <ResponsiveContainer width="100%" height={200} data-id="element-86">
                  <AreaChart data={marginData} data-id="element-87">
                    <defs data-id="element-88">
                      <linearGradient id="gmLight" x1="0" y1="0" x2="0" y2="1" data-id="element-89">
                        <stop offset="5%" stopColor="#4F46E5" stopOpacity={dark ? 0.4 : 0.2} data-id="element-90" />
                        <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} data-id="element-91" />
                      </linearGradient>
                      <linearGradient id="nmLight" x1="0" y1="0" x2="0" y2="1" data-id="element-92">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={dark ? 0.4 : 0.2} data-id="element-93" />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0} data-id="element-94" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-95" />
                    <XAxis dataKey="month" {...axisProps} data-id="element-96" />
                    <YAxis {...axisProps} tickFormatter={v => `${v}%`} data-id="element-97" />
                    <Tooltip content={<CustomTooltip suffix="%" data-id="element-99" />} data-id="element-98" />
                    <Area type="monotone" dataKey="grossMargin" stroke="#4F46E5" fill="url(#gmLight)" strokeWidth={2} name="Gross Margin" data-id="element-100" />
                    <Area type="monotone" dataKey="netMargin" stroke="#10B981" fill="url(#nmLight)" strokeWidth={2} name="Net Margin" data-id="element-101" />
                    <Line type="monotone" dataKey="salesPremium" stroke="#0EA5E9" strokeWidth={1.5} dot={false} strokeDasharray="4 2" name="Sales Premium" data-id="element-102" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard data-id="element-103">
                <SectionHeader title="Credit Pull Economics" subtitle="Total cost vs wasted pulls (non-converted)" data-id="element-104" />
                <ResponsiveContainer width="100%" height={200} data-id="element-105">
                  <BarChart data={creditPullData} data-id="element-106">
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-107" />
                    <XAxis dataKey="month" {...axisProps} data-id="element-108" />
                    <YAxis {...axisProps} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} data-id="element-109" />
                    <Tooltip content={<CustomTooltip formatter={(v: any) => `${v.toLocaleString()}`} data-id="element-111" />} data-id="element-110" />
                    <Bar dataKey="cost" fill={dark ? '#4338CA' : '#C7D2FE'} name="Total Pull Cost" radius={[3, 3, 0, 0]} data-id="element-112" />
                    <Bar dataKey="wasted" fill="#F43F5E" name="Wasted (No Convert)" radius={[3, 3, 0, 0]} data-id="element-113" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </div>}

        {/* ═══ PIPELINE ═══ */}
        {activeTab === 'pipeline' && <div className="space-y-5" data-id="element-114">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-115">
              {[{
            label: 'Total Leads YTD',
            value: '6,843',
            sub: 'avg 570/mo',
            color: 'text-indigo-600 dark:text-indigo-400'
          }, {
            label: 'Converted to App',
            value: '1,834',
            sub: '26.8% rate',
            color: 'text-sky-600 dark:text-sky-400'
          }, {
            label: 'Loans Registered',
            value: '1,412',
            sub: '77% of apps',
            color: 'text-emerald-600 dark:text-emerald-400'
          }, {
            label: 'Loans Closed',
            value: '2,847',
            sub: '$1.24B volume',
            color: 'text-violet-600 dark:text-violet-400'
          }].map((s, i) => <StatCard key={i} {...s} data-id="element-116" />)}
            </div>

            <div className="grid gap-5 lg:grid-cols-5" data-id="element-117">
              <ChartCard className="lg:col-span-3" data-id="element-118">
                <SectionHeader title="Monthly Pipeline Flow" subtitle="All 4 stages overlaid" action="View in Salesforce" data-id="element-119" />
                <ResponsiveContainer width="100%" height={300} data-id="element-120">
                  <ComposedChart data={pipelineData} data-id="element-121">
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-122" />
                    <XAxis dataKey="month" {...axisProps} data-id="element-123" />
                    <YAxis {...axisProps} data-id="element-124" />
                    <Tooltip content={<CustomTooltip data-id="element-126" />} data-id="element-125" />
                    <Legend wrapperStyle={legendStyle} iconType="circle" data-id="element-127" />
                    <Bar dataKey="leads" fill={dark ? '#3730A3' : '#E0E7FF'} name="Leads" radius={[2, 2, 0, 0]} data-id="element-128" />
                    <Bar dataKey="converted" fill={dark ? '#6366F1' : '#A5B4FC'} name="Converted" radius={[2, 2, 0, 0]} data-id="element-129" />
                    <Line type="monotone" dataKey="registered" stroke="#10B981" strokeWidth={2.5} dot={{
                  r: 3,
                  fill: '#10B981'
                }} name="Registered" data-id="element-130" />
                    <Line type="monotone" dataKey="closed" stroke={dark ? '#F8FAFC' : '#0F172A'} strokeWidth={2.5} dot={{
                  r: 3,
                  fill: dark ? '#F8FAFC' : '#0F172A'
                }} name="Closed" data-id="element-131" />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard className="lg:col-span-2" data-id="element-132">
                <SectionHeader title="Conversion Funnel" subtitle="Cumulative YTD" data-id="element-133" />
                <div className="space-y-4 py-2" data-id="element-134">
                  {[{
                label: 'Leads Received',
                value: 6843,
                pct: 100,
                color: '#4F46E5'
              }, {
                label: 'Applications Filed',
                value: 1834,
                pct: 26.8,
                color: '#0EA5E9'
              }, {
                label: 'Loans Registered',
                value: 1412,
                pct: 20.6,
                color: '#10B981'
              }, {
                label: 'Loans Closed',
                value: 2847,
                pct: 41.6,
                color: '#8B5CF6'
              }].map((f, i) => <div key={i} data-id="element-135">
                      <div className="mb-1.5 flex justify-between" data-id="element-136">
                        <span className="text-xs text-slate-600 dark:text-slate-400" data-id="element-137">
                          {f.label}
                        </span>
                        <span className="font-mono-num text-xs font-semibold text-slate-900 dark:text-slate-100" data-id="element-138">
                          {f.value.toLocaleString()}{' '}
                          <span className="text-slate-400 dark:text-slate-500" data-id="element-139">
                            ({f.pct}%)
                          </span>
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" data-id="element-140">
                        <div className="h-full rounded-full transition-all duration-1000" style={{
                    width: `${f.pct}%`,
                    background: f.color
                  }} data-id="element-141" />
                      </div>
                    </div>)}
                </div>
              </ChartCard>
            </div>

            <ChartCard data-id="element-142">
              <SectionHeader title="Credit Pull Analysis" subtitle="Cost optimization opportunities — pull costs vs. conversion outcomes" action="Export Report" data-id="element-143" />
              <div className="grid gap-6 lg:grid-cols-3" data-id="element-144">
                <div className="lg:col-span-2" data-id="element-145">
                  <ResponsiveContainer width="100%" height={220} data-id="element-146">
                    <BarChart data={creditPullData} data-id="element-147">
                      <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-148" />
                      <XAxis dataKey="month" {...axisProps} data-id="element-149" />
                      <YAxis {...axisProps} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} data-id="element-150" />
                      <Tooltip content={<CustomTooltip formatter={(v: any) => `${v.toLocaleString()}`} data-id="element-152" />} data-id="element-151" />
                      <Legend wrapperStyle={legendStyle} iconType="circle" data-id="element-153" />
                      <Bar dataKey="cost" fill={dark ? '#4338CA' : '#C7D2FE'} name="Total Cost" radius={[3, 3, 0, 0]} data-id="element-154" />
                      <Bar dataKey="wasted" fill="#F43F5E" name="Wasted Cost" radius={[3, 3, 0, 0]} data-id="element-155" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col justify-center gap-2" data-id="element-156">
                  {[{
                label: 'YTD Pull Cost',
                value: '$4.18M',
                color: 'text-indigo-600 dark:text-indigo-400'
              }, {
                label: 'Avg Cost / Pull',
                value: '$38.50',
                color: 'text-sky-600 dark:text-sky-400'
              }, {
                label: 'Wasted (No Convert)',
                value: '$1.24M',
                color: 'text-rose-600 dark:text-rose-400'
              }, {
                label: 'Cost / Closed Loan',
                value: '$1,469',
                color: 'text-emerald-600 dark:text-emerald-400'
              }, {
                label: 'Savings Opportunity',
                value: '↓ $312K',
                color: 'text-violet-600 dark:text-violet-400'
              }].map((s, i) => <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 dark:border-slate-800 dark:bg-slate-800/40" data-id="element-157">
                      <span className="text-xs text-slate-600 dark:text-slate-400" data-id="element-158">
                        {s.label}
                      </span>
                      <span className={`font-mono-num text-sm font-bold ${s.color}`} data-id="element-159">
                        {s.value}
                      </span>
                    </div>)}
                </div>
              </div>
            </ChartCard>
          </div>}

        {/* ═══ MARGINS ═══ */}
        {activeTab === 'margins' && <div className="space-y-5" data-id="element-160">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-id="element-161">
              {[{
            label: 'Avg Gross Margin',
            value: '2.34%',
            sub: 'per loan',
            color: 'text-indigo-600 dark:text-indigo-400'
          }, {
            label: 'Avg Net Margin',
            value: '1.61%',
            sub: 'after all costs',
            color: 'text-emerald-600 dark:text-emerald-400'
          }, {
            label: 'Sales Premium',
            value: '0.82%',
            sub: 'above par',
            color: 'text-sky-600 dark:text-sky-400'
          }, {
            label: 'Total Net Income YTD',
            value: '$19.9M',
            sub: 'all products',
            color: 'text-violet-600 dark:text-violet-400'
          }, {
            label: 'Revenue / Emp',
            value: '$186K',
            sub: 'annualized',
            color: 'text-amber-600 dark:text-amber-400'
          }].map((s, i) => <StatCard key={i} {...s} data-id="element-162" />)}
            </div>

            <div className="grid gap-5 lg:grid-cols-2" data-id="element-163">
              <ChartCard data-id="element-164">
                <SectionHeader title="Margin Components — Monthly" subtitle="Gross, Net, and Sales Premium %" data-id="element-165" />
                <ResponsiveContainer width="100%" height={280} data-id="element-166">
                  <LineChart data={marginData} data-id="element-167">
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-168" />
                    <XAxis dataKey="month" {...axisProps} data-id="element-169" />
                    <YAxis {...axisProps} tickFormatter={v => `${v}%`} domain={[0.5, 3]} data-id="element-170" />
                    <Tooltip content={<CustomTooltip suffix="%" data-id="element-172" />} data-id="element-171" />
                    <Legend wrapperStyle={legendStyle} iconType="circle" data-id="element-173" />
                    <Line type="monotone" dataKey="grossMargin" stroke="#4F46E5" strokeWidth={2.5} dot={{
                  r: 3,
                  fill: '#4F46E5'
                }} name="Gross Margin" data-id="element-174" />
                    <Line type="monotone" dataKey="netMargin" stroke="#10B981" strokeWidth={2.5} dot={{
                  r: 3,
                  fill: '#10B981'
                }} name="Net Margin" data-id="element-175" />
                    <Line type="monotone" dataKey="salesPremium" stroke="#0EA5E9" strokeWidth={2} strokeDasharray="5 3" dot={false} name="Sales Premium" data-id="element-176" />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard data-id="element-177">
                <SectionHeader title="P&L Bridge — YTD" subtitle="Revenue to net income waterfall" data-id="element-178" />
                <div className="space-y-3 py-2" data-id="element-179">
                  {[{
                label: 'Gross Revenue',
                value: '+$58.2M',
                color: '#10B981',
                bar: 100,
                bold: false
              }, {
                label: 'Cost of Funds',
                value: '-$21.4M',
                color: '#F43F5E',
                bar: 36.8,
                bold: false
              }, {
                label: 'Credit Pull Costs',
                value: '-$4.2M',
                color: '#F43F5E',
                bar: 7.2,
                bold: false
              }, {
                label: 'Origination Expenses',
                value: '-$7.1M',
                color: '#F43F5E',
                bar: 12.2,
                bold: false
              }, {
                label: 'Overhead & G&A',
                value: '-$5.6M',
                color: '#F43F5E',
                bar: 9.6,
                bold: false
              }, {
                label: 'Net Income',
                value: '$19.9M',
                color: '#4F46E5',
                bar: 34.2,
                bold: true
              }].map((r, i) => <div key={i} data-id="element-180">
                      <div className="mb-1 flex items-center justify-between" data-id="element-181">
                        <span className={`text-xs ${r.bold ? 'font-semibold text-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400'}`} data-id="element-182">
                          {r.label}
                        </span>
                        <span className="font-mono-num text-xs font-bold" style={{
                    color: r.color
                  }} data-id="element-183">
                          {r.value}
                        </span>
                      </div>
                      <div className={`overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800 ${r.bold ? 'h-2.5' : 'h-1.5'}`} data-id="element-184">
                        <div className="h-full rounded-full" style={{
                    width: `${r.bar}%`,
                    background: r.color
                  }} data-id="element-185" />
                      </div>
                    </div>)}
                </div>
              </ChartCard>
            </div>

            <ChartCard data-id="element-186">
              <SectionHeader title="Margin by Loan Type" subtitle="YTD net margin performance across product mix" data-id="element-187" />
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-188">
                {[{
              type: 'Conventional',
              gm: 2.41,
              nm: 1.72,
              vol: '$547M',
              count: 1247
            }, {
              type: 'FHA',
              gm: 1.98,
              nm: 1.21,
              vol: '$273M',
              count: 624
            }, {
              type: 'VA',
              gm: 2.18,
              nm: 1.48,
              vol: '$224M',
              count: 512
            }, {
              type: 'Jumbo',
              gm: 2.87,
              nm: 2.14,
              vol: '$124M',
              count: 285
            }, {
              type: 'USDA',
              gm: 1.74,
              nm: 0.98,
              vol: '$72M',
              count: 179
            }].map((p, i) => <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center dark:border-slate-800 dark:bg-slate-800/40" data-id="element-189">
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" data-id="element-190">
                      {p.type}
                    </div>
                    <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400" data-id="element-191">
                      Gross Margin
                    </div>
                    <div className="font-mono-num text-xl font-bold text-indigo-600 dark:text-indigo-400" data-id="element-192">
                      {p.gm}%
                    </div>
                    <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400" data-id="element-193">
                      Net Margin
                    </div>
                    <div className="font-mono-num text-base font-bold text-emerald-600 dark:text-emerald-400" data-id="element-194">
                      {p.nm}%
                    </div>
                    <div className="mt-3 border-t border-slate-200 pt-2 dark:border-slate-700" data-id="element-195">
                      <div className="font-mono-num text-xs font-semibold text-slate-900 dark:text-slate-100" data-id="element-196">
                        {p.vol}
                      </div>
                      <div className="text-[10px] text-slate-400 dark:text-slate-500" data-id="element-197">
                        {p.count.toLocaleString()} loans
                      </div>
                    </div>
                  </div>)}
              </div>
            </ChartCard>
          </div>}

        {/* ═══ WAREHOUSE ═══ */}
        {activeTab === 'warehouse' && <div className="space-y-5" data-id="element-198">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-199">
              {[{
            label: 'Total Line Capacity',
            value: '$25M',
            sub: 'approved limit',
            color: 'text-indigo-600 dark:text-indigo-400'
          }, {
            label: 'Current Utilization',
            value: '$19.8M',
            sub: '79.2% used',
            color: 'text-sky-600 dark:text-sky-400'
          }, {
            label: 'Avg Dwell Time',
            value: '14.3 days',
            sub: 'per loan on line',
            color: 'text-emerald-600 dark:text-emerald-400'
          }, {
            label: 'Net Interest Position',
            value: '+$182K',
            sub: 'gain vs cost YTD',
            color: 'text-violet-600 dark:text-violet-400'
          }].map((s, i) => <StatCard key={i} {...s} data-id="element-200" />)}
            </div>

            <div className="grid gap-5 lg:grid-cols-5" data-id="element-201">
              <ChartCard className="lg:col-span-3" data-id="element-202">
                <SectionHeader title="Warehouse Line Utilization" subtitle="Monthly usage vs. $25M ceiling (in $M)" data-id="element-203" />
                <ResponsiveContainer width="100%" height={280} data-id="element-204">
                  <ComposedChart data={warehouseData} data-id="element-205">
                    <defs data-id="element-206">
                      <linearGradient id="whLight" x1="0" y1="0" x2="0" y2="1" data-id="element-207">
                        <stop offset="5%" stopColor="#4F46E5" stopOpacity={dark ? 0.5 : 0.25} data-id="element-208" />
                        <stop offset="95%" stopColor="#4F46E5" stopOpacity={0.02} data-id="element-209" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-210" />
                    <XAxis dataKey="month" {...axisProps} data-id="element-211" />
                    <YAxis {...axisProps} tickFormatter={v => `$${v}M`} domain={[0, 28]} data-id="element-212" />
                    <Tooltip content={<CustomTooltip formatter={(v: any, n: any) => n === 'Line Limit' ? `${v}M (cap)` : `${v}M`} data-id="element-214" />} data-id="element-213" />
                    <Legend wrapperStyle={legendStyle} iconType="circle" data-id="element-215" />
                    <Area type="monotone" dataKey="lineUsed" fill="url(#whLight)" stroke="#4F46E5" strokeWidth={2.5} name="Line Used ($M)" data-id="element-216" />
                    <Line type="monotone" dataKey="lineLimit" stroke="#F43F5E" strokeWidth={1.5} strokeDasharray="6 3" dot={false} name="Line Limit" data-id="element-217" />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard className="lg:col-span-2" data-id="element-218">
                <SectionHeader title="Interest P&L" subtitle="Cost vs. gain on warehouse" data-id="element-219" />
                <ResponsiveContainer width="100%" height={180} data-id="element-220">
                  <BarChart data={warehouseData} data-id="element-221">
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-222" />
                    <XAxis dataKey="month" {...axisProps} data-id="element-223" />
                    <YAxis {...axisProps} tickFormatter={v => `${v}%`} data-id="element-224" />
                    <Tooltip content={<CustomTooltip formatter={(v: any) => `${v}%`} data-id="element-226" />} data-id="element-225" />
                    <Legend wrapperStyle={legendStyle} iconType="circle" data-id="element-227" />
                    <Bar dataKey="interestGain" fill="#10B981" name="Interest Gain %" radius={[3, 3, 0, 0]} data-id="element-228" />
                    <Bar dataKey="interestCost" fill="#F43F5E" name="Interest Cost %" radius={[3, 3, 0, 0]} data-id="element-229" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-500/30 dark:bg-emerald-500/10" data-id="element-230">
                  <div className="text-[10px] font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-400" data-id="element-231">
                    YTD Net Interest Position
                  </div>
                  <div className="font-mono-num text-2xl font-bold text-emerald-700 dark:text-emerald-300" data-id="element-232">
                    +$182,400
                  </div>
                  <div className="mt-0.5 text-xs text-emerald-700/80 dark:text-emerald-300/80" data-id="element-233">
                    Gain exceeds borrowing cost by avg 0.14%/mo
                  </div>
                </div>
              </ChartCard>
            </div>

            <ChartCard data-id="element-234">
              <SectionHeader title="Warehouse Line Optimization Alerts" subtitle="AI-driven recommendations to improve line efficiency" data-id="element-235" />
              <div className="grid gap-4 md:grid-cols-3" data-id="element-236">
                {[{
              severity: 'warn',
              Icon: AlertTriangleIcon,
              title: 'High Dwell Risk',
              body: '14 loans averaging 22+ days on line. Accelerate secondary market delivery to free $3.1M capacity.',
              action: 'View loans'
            }, {
              severity: 'info',
              Icon: LightbulbIcon,
              title: 'Rate Lock Alignment',
              body: 'Current SOFR spread favorable. Consider increasing pull-through to maximize interest differential.',
              action: 'Model scenario'
            }, {
              severity: 'ok',
              Icon: CheckCircle2Icon,
              title: 'Utilization Healthy',
              body: '79.2% utilization is within optimal 75–85% band. No immediate capacity action required.',
              action: 'Set alert'
            }].map((a, i) => {
              const palette = {
                warn: {
                  bg: 'bg-amber-50 dark:bg-amber-500/10',
                  br: 'border-amber-200 dark:border-amber-500/30',
                  tx: 'text-amber-700 dark:text-amber-400'
                },
                info: {
                  bg: 'bg-sky-50 dark:bg-sky-500/10',
                  br: 'border-sky-200 dark:border-sky-500/30',
                  tx: 'text-sky-700 dark:text-sky-400'
                },
                ok: {
                  bg: 'bg-emerald-50 dark:bg-emerald-500/10',
                  br: 'border-emerald-200 dark:border-emerald-500/30',
                  tx: 'text-emerald-700 dark:text-emerald-400'
                }
              }[a.severity as 'warn' | 'info' | 'ok'];
              return <div key={i} className={`rounded-xl border ${palette.br} ${palette.bg} p-4`} data-id="element-237">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-lg bg-white dark:bg-slate-900 ${palette.tx}`} data-id="element-238">
                        <a.Icon className="h-4 w-4" data-id="element-239" />
                      </div>
                      <div className={`mt-3 text-sm font-semibold ${palette.tx}`} data-id="element-240">
                        {a.title}
                      </div>
                      <p className="mt-1.5 text-xs leading-relaxed text-slate-600 dark:text-slate-300" data-id="element-241">
                        {a.body}
                      </p>
                      <button className={`mt-3 inline-flex items-center gap-1 text-[11px] font-semibold ${palette.tx} hover:underline`} data-id="element-242">
                        {a.action} <ArrowUpRightIcon className="h-3 w-3" data-id="element-243" />
                      </button>
                    </div>;
            })}
              </div>
            </ChartCard>
          </div>}

        {/* ═══ LEAD INTELLIGENCE ═══ */}
        {activeTab === 'leads' && <div className="space-y-5" data-id="element-244">
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-5 py-3.5 dark:border-indigo-500/30 dark:bg-indigo-500/10" data-id="element-245">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-indigo-600 dark:bg-slate-900 dark:text-indigo-400" data-id="element-246">
                <MegaphoneIcon className="h-4 w-4" data-id="element-247" />
              </div>
              <div className="flex-1" data-id="element-248">
                <div className="text-sm font-semibold text-indigo-800 dark:text-indigo-300" data-id="element-249">
                  Lead Intelligence — Acquisition & Funnel Analytics
                </div>
                <div className="text-xs text-indigo-700/80 dark:text-indigo-300/80" data-id="element-250">
                  6,843 leads YTD across 6 channels · $4.18M total spend ·
                  Tracking cost-to-close by source
                </div>
              </div>
              <button className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 dark:border-indigo-500/30 dark:bg-slate-900 dark:text-indigo-400 dark:hover:bg-slate-800" data-id="element-251">
                <ArrowUpRightIcon className="h-3.5 w-3.5" data-id="element-252" />
                Open in CRM
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-253">
              <StatCard label="Marketing Spend YTD" value="$4.18M" sub="across 6 channels" color="text-indigo-600 dark:text-indigo-400" data-id="element-254" />
              <StatCard label="Avg Cost per Lead" value="$41.20" sub="blended CPL" color="text-sky-600 dark:text-sky-400" data-id="element-255" />
              <StatCard label="Cost per Closed Loan" value="$1,469" sub="acquisition cost" color="text-emerald-600 dark:text-emerald-400" data-id="element-256" />
              <StatCard label="Lead-to-Close Rate" value="11.6%" sub="from captured to funded" color="text-violet-600 dark:text-violet-400" data-id="element-257" />
            </div>

            <div className="grid gap-5 lg:grid-cols-5" data-id="element-258">
              <ChartCard className="lg:col-span-3" data-id="element-259">
                <SectionHeader title="Lead Source Performance" subtitle="Volume, cost per lead, and downstream conversion" action="Export breakdown" data-id="element-260" />
                <div className="overflow-x-auto" data-id="element-261">
                  <table className="w-full border-collapse" data-id="element-262">
                    <thead data-id="element-263">
                      <tr className="border-b border-slate-200 dark:border-slate-800" data-id="element-264">
                        {['Source', 'Leads', 'CPL', 'Qualified', 'Closed', 'Close Rate', 'Cost / Close'].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400" data-id="element-265">
                            {h}
                          </th>)}
                      </tr>
                    </thead>
                    <tbody data-id="element-266">
                      {leadSources.map((r, i) => {
                    const closeRate = r.closed / r.leads * 100;
                    const costPerClose = r.leads * r.cpl / r.closed;
                    return <tr key={i} className="border-b border-slate-100 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/40" data-id="element-267">
                            <td className="px-3 py-3" data-id="element-268">
                              <div className="flex items-center gap-2" data-id="element-269">
                                <span className="h-2.5 w-2.5 rounded-sm" style={{
                            background: r.color
                          }} data-id="element-270" />
                                <span className="text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-271">
                                  {r.source}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-3 font-mono-num text-sm text-slate-900 dark:text-slate-100" data-id="element-272">
                              {r.leads.toLocaleString()}
                            </td>
                            <td className="px-3 py-3 font-mono-num text-sm font-semibold text-indigo-600 dark:text-indigo-400" data-id="element-273">
                              ${r.cpl}
                            </td>
                            <td className="px-3 py-3 font-mono-num text-sm text-slate-700 dark:text-slate-300" data-id="element-274">
                              {r.qualified.toLocaleString()}
                            </td>
                            <td className="px-3 py-3 font-mono-num text-sm font-semibold text-emerald-600 dark:text-emerald-400" data-id="element-275">
                              {r.closed.toLocaleString()}
                            </td>
                            <td className="px-3 py-3" data-id="element-276">
                              <div className="flex items-center gap-2" data-id="element-277">
                                <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" data-id="element-278">
                                  <div className="h-full rounded-full" style={{
                              width: `${Math.min(closeRate * 3, 100)}%`,
                              background: closeRate >= 20 ? '#10B981' : closeRate >= 10 ? '#4F46E5' : '#F59E0B'
                            }} data-id="element-279" />
                                </div>
                                <span className="font-mono-num text-xs text-slate-600 dark:text-slate-400" data-id="element-280">
                                  {closeRate.toFixed(1)}%
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-3 font-mono-num text-sm font-bold text-slate-900 dark:text-slate-100" data-id="element-281">
                              ${Math.round(costPerClose).toLocaleString()}
                            </td>
                          </tr>;
                  })}
                    </tbody>
                  </table>
                </div>
              </ChartCard>

              <ChartCard className="lg:col-span-2" data-id="element-282">
                <SectionHeader title="Cost per Closed Loan" subtitle="By acquisition channel (lower is better)" data-id="element-283" />
                <ResponsiveContainer width="100%" height={260} data-id="element-284">
                  <BarChart data={leadSources.map(s => ({
                source: s.source,
                costPerClose: Math.round(s.leads * s.cpl / s.closed),
                color: s.color
              }))} layout="vertical" margin={{
                left: 8,
                right: 16,
                top: 8,
                bottom: 8
              }} data-id="element-285">
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} horizontal={false} data-id="element-286" />
                    <XAxis type="number" {...axisProps} tickFormatter={v => `$${(v / 1000).toFixed(1)}K`} data-id="element-287" />
                    <YAxis type="category" dataKey="source" {...axisProps} width={80} data-id="element-288" />
                    <Tooltip content={<CustomTooltip formatter={(v: any) => `${v.toLocaleString()}`} data-id="element-290" />} data-id="element-289" />
                    <Bar dataKey="costPerClose" radius={[0, 4, 4, 0]} data-id="element-291">
                      {leadSources.map((s, i) => <Cell key={i} fill={s.color} data-id="element-292" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <ChartCard data-id="element-293">
              <SectionHeader title="Pipeline Progression by Source" subtitle="How far each channel's leads travel — captured → qualified → closed" data-id="element-294" />
              <ResponsiveContainer width="100%" height={280} data-id="element-295">
                <BarChart data={leadSources} data-id="element-296">
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-297" />
                  <XAxis dataKey="source" {...axisProps} data-id="element-298" />
                  <YAxis {...axisProps} data-id="element-299" />
                  <Tooltip content={<CustomTooltip data-id="element-301" />} data-id="element-300" />
                  <Legend wrapperStyle={legendStyle} iconType="circle" data-id="element-302" />
                  <Bar dataKey="leads" fill={dark ? '#3730A3' : '#C7D2FE'} name="Leads Captured" radius={[3, 3, 0, 0]} data-id="element-303" />
                  <Bar dataKey="qualified" fill="#4F46E5" name="Qualified" radius={[3, 3, 0, 0]} data-id="element-304" />
                  <Bar dataKey="closed" fill="#10B981" name="Closed" radius={[3, 3, 0, 0]} data-id="element-305" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <div className="grid gap-5 lg:grid-cols-5" data-id="element-306">
              <ChartCard className="lg:col-span-2" data-id="element-307">
                <SectionHeader title="Lead-to-Close Funnel" subtitle="Stage-by-stage drop-off (YTD)" data-id="element-308" />
                <div className="space-y-4 py-2" data-id="element-309">
                  {leadStageFunnel.map((f, i) => {
                const dropFromPrev = i === 0 ? null : (leadStageFunnel[i - 1].count - f.count) / leadStageFunnel[i - 1].count * 100;
                return <div key={i} data-id="element-310">
                        <div className="mb-1.5 flex items-center justify-between" data-id="element-311">
                          <span className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300" data-id="element-312">
                            <span className="font-mono-num text-[10px] text-slate-400 dark:text-slate-500" data-id="element-313">
                              0{i + 1}
                            </span>
                            {f.stage}
                          </span>
                          <div className="flex items-center gap-2" data-id="element-314">
                            {dropFromPrev !== null && <span className="font-mono-num text-[10px] text-rose-600 dark:text-rose-400" data-id="element-315">
                                ↓ {dropFromPrev.toFixed(0)}%
                              </span>}
                            <span className="font-mono-num text-xs font-semibold text-slate-900 dark:text-slate-100" data-id="element-316">
                              {f.count.toLocaleString()}{' '}
                              <span className="text-slate-400 dark:text-slate-500" data-id="element-317">
                                ({f.pct}%)
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="h-2.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" data-id="element-318">
                          <div className="h-full rounded-full transition-all duration-1000" style={{
                      width: `${f.pct}%`,
                      background: f.color
                    }} data-id="element-319" />
                        </div>
                      </div>;
              })}
                </div>
              </ChartCard>

              <ChartCard className="lg:col-span-3" data-id="element-320">
                <SectionHeader title="Monthly Lead Cost vs. Closed Volume" subtitle="Marketing spend ($) and resulting closed loans" data-id="element-321" />
                <ResponsiveContainer width="100%" height={260} data-id="element-322">
                  <ComposedChart data={monthlyLeadEconomics} data-id="element-323">
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-324" />
                    <XAxis dataKey="month" {...axisProps} data-id="element-325" />
                    <YAxis yAxisId="left" {...axisProps} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} data-id="element-326" />
                    <YAxis yAxisId="right" orientation="right" {...axisProps} data-id="element-327" />
                    <Tooltip content={<CustomTooltip formatter={(v: any, n: any) => n === 'Marketing Spend' ? `${v.toLocaleString()}` : v.toLocaleString()} data-id="element-329" />} data-id="element-328" />
                    <Legend wrapperStyle={legendStyle} iconType="circle" data-id="element-330" />
                    <Bar yAxisId="left" dataKey="spend" fill={dark ? '#4338CA' : '#C7D2FE'} name="Marketing Spend" radius={[3, 3, 0, 0]} data-id="element-331" />
                    <Line yAxisId="right" type="monotone" dataKey="closed" stroke="#10B981" strokeWidth={2.5} dot={{
                  r: 3,
                  fill: '#10B981'
                }} name="Closed Loans" data-id="element-332" />
                    <Line yAxisId="right" type="monotone" dataKey="leads" stroke="#0EA5E9" strokeWidth={2} strokeDasharray="4 3" dot={false} name="Lead Volume" data-id="element-333" />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <div className="grid gap-5 lg:grid-cols-2" data-id="element-334">
              <ChartCard data-id="element-335">
                <SectionHeader title="Lead Response Time" subtitle="Time from capture to first contact attempt" data-id="element-336" />
                <ResponsiveContainer width="100%" height={200} data-id="element-337">
                  <BarChart data={leadAging} data-id="element-338">
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} data-id="element-339" />
                    <XAxis dataKey="bucket" {...axisProps} data-id="element-340" />
                    <YAxis {...axisProps} data-id="element-341" />
                    <Tooltip content={<CustomTooltip formatter={(v: any) => v.toLocaleString()} data-id="element-343" />} data-id="element-342" />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} data-id="element-344">
                      {leadAging.map((b, i) => <Cell key={i} fill={b.color} data-id="element-345" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-346">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 dark:border-emerald-500/30 dark:bg-emerald-500/10" data-id="element-347">
                    <div className="text-[10px] text-emerald-700 dark:text-emerald-400" data-id="element-348">
                      ≤ 1 hr
                    </div>
                    <div className="font-mono-num text-sm font-bold text-emerald-700 dark:text-emerald-300" data-id="element-349">
                      35.2%
                    </div>
                  </div>
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2 dark:border-indigo-500/30 dark:bg-indigo-500/10" data-id="element-350">
                    <div className="text-[10px] text-indigo-700 dark:text-indigo-400" data-id="element-351">
                      ≤ 24 hr
                    </div>
                    <div className="font-mono-num text-sm font-bold text-indigo-700 dark:text-indigo-300" data-id="element-352">
                      80.1%
                    </div>
                  </div>
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 dark:border-rose-500/30 dark:bg-rose-500/10" data-id="element-353">
                    <div className="text-[10px] text-rose-700 dark:text-rose-400" data-id="element-354">
                      {'>'} 3 days
                    </div>
                    <div className="font-mono-num text-sm font-bold text-rose-700 dark:text-rose-300" data-id="element-355">
                      8.1%
                    </div>
                  </div>
                </div>
              </ChartCard>

              <ChartCard data-id="element-356">
                <SectionHeader title="Channel Mix & Spend Allocation" subtitle="Share of marketing budget by source" data-id="element-357" />
                <div className="flex items-center gap-4" data-id="element-358">
                  <ResponsiveContainer width="50%" height={200} data-id="element-359">
                    <PieChart data-id="element-360">
                      <Pie data={leadSources.map(s => ({
                    name: s.source,
                    value: s.leads * s.cpl,
                    color: s.color
                  }))} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" data-id="element-361">
                        {leadSources.map((s, i) => <Cell key={i} fill={s.color} stroke={pieStroke} strokeWidth={2} data-id="element-362" />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip formatter={(v: any) => `${v.toLocaleString()}`} data-id="element-364" />} data-id="element-363" />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5" data-id="element-365">
                    {leadSources.map((s, i) => {
                  const spend = s.leads * s.cpl;
                  const totalSpend = leadSources.reduce((sum, x) => sum + x.leads * x.cpl, 0);
                  const pct = spend / totalSpend * 100;
                  return <div key={i} className="flex items-center gap-2 text-xs" data-id="element-366">
                          <span className="h-2 w-2 rounded-sm" style={{
                      background: s.color
                    }} data-id="element-367" />
                          <span className="flex-1 text-slate-600 dark:text-slate-400" data-id="element-368">
                            {s.source}
                          </span>
                          <span className="font-mono-num font-semibold text-slate-900 dark:text-slate-100" data-id="element-369">
                            ${(spend / 1000).toFixed(0)}K
                          </span>
                          <span className="font-mono-num text-slate-400 dark:text-slate-500 w-10 text-right" data-id="element-370">
                            {pct.toFixed(0)}%
                          </span>
                        </div>;
                })}
                  </div>
                </div>
              </ChartCard>
            </div>

            <ChartCard data-id="element-371">
              <SectionHeader title="Lead Intelligence Insights" subtitle="AI-driven recommendations to optimize acquisition spend" data-id="element-372" />
              <div className="grid gap-4 md:grid-cols-3" data-id="element-373">
                {[{
              severity: 'ok',
              Icon: CheckCircle2Icon,
              title: 'Referral ROI Leader',
              body: 'Referral channel delivers 31.8% close rate at just $57 cost-per-close. Consider doubling referral incentive budget for Q3.',
              action: 'Increase budget'
            }, {
              severity: 'warn',
              Icon: AlertTriangleIcon,
              title: 'Aggregator Inefficiency',
              body: 'Aggregator leads cost $2,765 per closed loan — 188% above blended average. Recommend reducing spend by 40% and reallocating to organic.',
              action: 'View loans'
            }, {
              severity: 'info',
              Icon: ClockIcon,
              title: 'Response Time Lever',
              body: 'Leads contacted within 1 hour convert 3.2× higher. 8.1% of leads still aging past 3 days — automate routing to recover ~$240K revenue.',
              action: 'Configure routing'
            }].map((a, i) => {
              const palette = {
                warn: {
                  bg: 'bg-amber-50 dark:bg-amber-500/10',
                  br: 'border-amber-200 dark:border-amber-500/30',
                  tx: 'text-amber-700 dark:text-amber-400'
                },
                info: {
                  bg: 'bg-sky-50 dark:bg-sky-500/10',
                  br: 'border-sky-200 dark:border-sky-500/30',
                  tx: 'text-sky-700 dark:text-sky-400'
                },
                ok: {
                  bg: 'bg-emerald-50 dark:bg-emerald-500/10',
                  br: 'border-emerald-200 dark:border-emerald-500/30',
                  tx: 'text-emerald-700 dark:text-emerald-400'
                }
              }[a.severity as 'warn' | 'info' | 'ok'];
              return <div key={i} className={`rounded-xl border ${palette.br} ${palette.bg} p-4`} data-id="element-374">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-lg bg-white dark:bg-slate-900 ${palette.tx}`} data-id="element-375">
                        <a.Icon className="h-4 w-4" data-id="element-376" />
                      </div>
                      <div className={`mt-3 text-sm font-semibold ${palette.tx}`} data-id="element-377">
                        {a.title}
                      </div>
                      <p className="mt-1.5 text-xs leading-relaxed text-slate-600 dark:text-slate-300" data-id="element-378">
                        {a.body}
                      </p>
                      <button className={`mt-3 inline-flex items-center gap-1 text-[11px] font-semibold ${palette.tx} hover:underline`} data-id="element-379">
                        {a.action} <ArrowUpRightIcon className="h-3 w-3" data-id="element-380" />
                      </button>
                    </div>;
            })}
              </div>
            </ChartCard>
          </div>}
      </main>
    </div>;
}